import { useEffect, useState, useMemo } from 'react';
import { Download, Printer } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { downloadCsv } from './csvExport';
import { formatElapsed } from './timeAgo';
import MetricCard from './MetricCard';
import TopRevenueDays from './TopRevenueDays';
import FailureAnalysis from './FailureAnalysis';
import DateRangeSelector, { getFilterDates } from './DateRangeSelector';
import type { DateFilter, DateRange } from './DateRangeSelector';
import type { Order } from '@shared/types';

interface PeriodRow {
  period: string;
  orders: number;
  revenue: number;
  avg: number;
  successRate: number;
}

interface BreakdownRow {
  name: string;
  orders: number;
  revenue: number;
}

function isStripeOrder(o: Order) {
  return o.provider === 'stripe' || o.payment_provider === 'stripe';
}

function getPreviousPeriodMs(range: DateRange): number | null {
  if (range === '7d') return 7 * 86_400_000;
  if (range === '30d') return 30 * 86_400_000;
  if (range === '60d') return 60 * 86_400_000;
  if (range === '90d') return 90 * 86_400_000;
  return null;
}

function aggregatePeriods(orders: Order[], range: DateRange): PeriodRow[] {
  const buckets: Record<string, Order[]> = {};
  for (const o of orders) {
    const d = new Date(o.created_at);
    let key: string;
    if (range === '7d' || range === '30d') {
      key = d.toISOString().slice(0, 10);
    } else if (range === '90d') {
      const week = new Date(d);
      week.setDate(week.getDate() - week.getDay());
      key = `Week of ${week.toISOString().slice(0, 10)}`;
    } else {
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(o);
  }
  return Object.entries(buckets)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([period, items]) => {
      const completed = items.filter((o) => o.status === 'completed');
      const revenue = completed.reduce((s, o) => s + (o.verified_amount || o.expected_amount), 0);
      return {
        period,
        orders: items.length,
        revenue,
        avg: completed.length > 0 ? revenue / completed.length : 0,
        successRate: items.length > 0 ? (completed.length / items.length) * 100 : 0,
      };
    });
}

function buildBreakdown(orders: Order[]): BreakdownRow[] {
  const map: Record<string, { count: number; revenue: number }> = {};
  for (const o of orders) {
    const prov = o.provider || 'Unknown';
    if (!map[prov]) map[prov] = { count: 0, revenue: 0 };
    map[prov].count++;
    if (o.status === 'completed') {
      map[prov].revenue += o.verified_amount || o.expected_amount;
    }
  }
  return Object.entries(map)
    .sort(([, a], [, b]) => b.revenue - a.revenue)
    .map(([name, v]) => ({ name, orders: v.count, revenue: v.revenue }));
}

function isSquareOrder(o: Order) {
  return o.provider === 'square' || o.payment_provider === 'square';
}

function isCryptoOrder(o: Order) {
  return o.provider === 'crypto' || o.payment_provider === 'crypto';
}

interface Props {
  solution: 'crypto' | 'stripe' | 'square';
}

export default function Reports({ solution }: Props) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ range: '30d' });

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { start, end } = getFilterDates(dateFilter);
      let query = supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);
      if (start) query = query.gte('created_at', start);
      if (end) query = query.lte('created_at', end);
      const { data } = await query;
      if (data) {
        let result = data as unknown as Order[];
        if (solution === 'crypto') {
          result = result.filter(isCryptoOrder);
        } else if (solution === 'square') {
          result = result.filter(isSquareOrder);
        } else {
          result = result.filter(isStripeOrder);
        }
        setOrders(result);
      }
      setLoading(false);
    }
    load();
  }, [dateFilter, solution]);

  const completed = orders.filter((o) => o.status === 'completed');
  const totalRevenue = completed.reduce((s, o) => s + (o.verified_amount || o.expected_amount), 0);
  const avgOrder = completed.length > 0 ? totalRevenue / completed.length : 0;
  const successRate = orders.length > 0 ? (completed.length / orders.length) * 100 : 0;
  const conversionRate = orders.length > 0
    ? (completed.length / orders.length) * 100
    : 0;

  const settlementLag = useMemo(() => {
    const settled = completed.filter((o) => o.completed_at);
    if (settled.length === 0) return null;
    const totalMs = settled.reduce((sum, o) => {
      return sum + (new Date(o.completed_at!).getTime() - new Date(o.created_at).getTime());
    }, 0);
    return totalMs / settled.length;
  }, [completed]);

  const aovTrend = useMemo(() => {
    const periodMs = getPreviousPeriodMs(dateFilter.range);
    if (!periodMs) return null;
    const { start: currentStart } = getFilterDates(dateFilter);
    if (!currentStart) return null;

    const currentStartMs = new Date(currentStart).getTime();
    const prevStartMs = currentStartMs - periodMs;

    const prevCompleted = completed.filter((o) => {
      const t = new Date(o.created_at).getTime();
      return t >= prevStartMs && t < currentStartMs;
    });
    if (prevCompleted.length === 0) return null;

    const prevRev = prevCompleted.reduce((s, o) => s + (o.verified_amount || o.expected_amount), 0);
    const prevAov = prevRev / prevCompleted.length;
    if (prevAov === 0) return null;

    const change = ((avgOrder - prevAov) / prevAov) * 100;
    return { value: `${change >= 0 ? '+' : ''}${change.toFixed(1)}% vs prev`, positive: change >= 0 };
  }, [completed, dateFilter, avgOrder]);

  const periods = aggregatePeriods(orders, dateFilter.range);
  const breakdown = buildBreakdown(orders);

  const handleExportCsv = () => {
    const headers = ['Period', 'Orders', 'Revenue', 'Avg Order Value', 'Success Rate'];
    const rows = periods.map((r) => [
      r.period,
      String(r.orders),
      r.revenue.toFixed(2),
      r.avg.toFixed(2),
      `${r.successRate.toFixed(1)}%`,
    ]);
    downloadCsv(headers, rows, `report-${solution}-${dateFilter.range}`);
  };

  const handlePrint = () => window.print();

  const thClass = 'text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium';
  const tdClass = 'px-4 py-2.5 font-mono text-xs';
  const tdMuted = 'px-4 py-2.5 font-mono text-xs';

  return (
    <div className="p-6 print:p-0 print:bg-white">
      <div className="flex items-center justify-between mb-5 print:hidden">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>
          {solution === 'stripe' ? 'Stripe Reports' : solution === 'square' ? 'Square Reports' : 'Crypto Reports'}
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            title="Print"
            className="p-1.5 rounded transition-colors"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
          >
            <Printer size={14} />
          </button>
          <button
            onClick={handleExportCsv}
            disabled={periods.length === 0}
            title="Export CSV"
            className="p-1.5 rounded transition-colors disabled:opacity-40"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
          >
            <Download size={14} />
          </button>
          <DateRangeSelector value={dateFilter} onChange={setDateFilter} />
        </div>
      </div>

      {loading ? (
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading reports...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <MetricCard label={'\u03A3 Revenue'} value={`$${totalRevenue.toFixed(2)}`} />
            <MetricCard label="Orders" value={String(orders.length)} />
            <MetricCard label="Avg Order" value={`$${avgOrder.toFixed(2)}`} trend={aovTrend} />
            <MetricCard label="Conversion" value={`${conversionRate.toFixed(1)}%`} />
            <MetricCard
              label="Avg Settlement"
              value={settlementLag !== null ? formatElapsed(settlementLag) : '--'}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <TopRevenueDays orders={orders} />
            <FailureAnalysis orders={orders} />
          </div>

          <div className="rounded overflow-x-auto mb-6" style={{ border: '1px solid var(--dash-border)' }}>
            <div className="font-mono text-[10px] uppercase tracking-widest px-4 pt-3 pb-1" style={{ color: 'var(--dash-text-dim)' }}>
              {dateFilter.range === '7d' || dateFilter.range === '30d' ? 'Daily' : dateFilter.range === '90d' ? 'Weekly' : 'Monthly'} Breakdown
            </div>
            {periods.length === 0 ? (
              <div className="p-6 text-center">
                <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>No data for this period.</p>
              </div>
            ) : (
              <table className="w-full text-sm font-sans">
                <thead>
                  <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Period</th>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Orders</th>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Revenue</th>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Avg Value</th>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Success</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((row, i) => (
                    <tr
                      key={row.period}
                      className="transition-colors"
                      style={{ background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)', borderBottom: '1px solid var(--dash-border-light)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                    >
                      <td className={tdClass} style={{ color: 'var(--dash-text-tertiary)' }}>{row.period}</td>
                      <td className={tdClass} style={{ color: 'var(--dash-text-tertiary)' }}>{row.orders}</td>
                      <td className={tdClass} style={{ color: 'var(--dash-text-tertiary)' }}>${row.revenue.toFixed(2)}</td>
                      <td className={tdMuted} style={{ color: 'var(--dash-text-dim)' }}>${row.avg.toFixed(2)}</td>
                      <td className={tdMuted} style={{ color: 'var(--dash-text-dim)' }}>
                        <span className={row.successRate >= 80 ? 'text-emerald-400' : row.successRate >= 50 ? 'text-amber-400' : 'text-red-400'}>
                          {row.successRate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
            <div className="font-mono text-[10px] uppercase tracking-widest px-4 pt-3 pb-1" style={{ color: 'var(--dash-text-dim)' }}>
              Provider Breakdown
            </div>
            {breakdown.length === 0 ? (
              <div className="p-6 text-center">
                <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>No breakdown data available.</p>
              </div>
            ) : (
              <table className="w-full text-sm font-sans">
                <thead>
                  <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Provider</th>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Orders</th>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Revenue</th>
                    <th className={thClass} style={{ color: 'var(--dash-text-dim)' }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((row, i) => {
                    const share = orders.length > 0 ? (row.orders / orders.length) * 100 : 0;
                    return (
                      <tr
                        key={row.name}
                        className="transition-colors"
                        style={{ background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)', borderBottom: '1px solid var(--dash-border-light)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                      >
                        <td className={tdClass} style={{ color: 'var(--dash-text-tertiary)' }}>{row.name}</td>
                        <td className={tdClass} style={{ color: 'var(--dash-text-tertiary)' }}>{row.orders}</td>
                        <td className={tdClass} style={{ color: 'var(--dash-text-tertiary)' }}>${row.revenue.toFixed(2)}</td>
                        <td className={tdMuted} style={{ color: 'var(--dash-text-dim)' }}>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 rounded overflow-hidden" style={{ background: 'var(--dash-border-light)' }}>
                              <div
                                className="h-full rounded"
                                style={{ width: `${share}%`, background: 'var(--dash-text-dim)' }}
                              />
                            </div>
                            <span>{share.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
